import React from 'react';
import { useTranslation } from 'react-i18next';
import U2000CheckBox from '../../../u2000Core/components/formControls/checkbox/U2000CheckBox';
import type { U2000FormStateHandlers } from '../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { U2000_TIMESHEET_WEEK_DAYS, type U2000TimesheetWeekDay } from '../../../u2000Core/tools/U2000TimesheetHonorariumDayTotals';
import type { U3130HonorariumItem, U3130Timesheet } from '../../U3130TimesheetModel';
import { u3130HonorariumSubprojectLabel, u3130HonorariumTaskLabel } from '../../tools/u3130GroupHonorariumForDisplay';
import { u3130TaskWorkNatureDescriptionForLanguage } from '../../tools/u3130HonorariumWorkNatureDisplay';
import {
  U3130_HONORARIUM_CELL_X,
  U3130_HONORARIUM_CHECKBOX_TD_CLASS,
  U3130_HONORARIUM_EXPANDED_ACCENT_CELL_CLASS,
  U3130_HONORARIUM_EXPANDED_CHILD_BOX_CLASS,
  U3130_HONORARIUM_EXPANDED_CHILD_ROW_CLASS,
  U3130_HONORARIUM_HOUR_TD_CLASS,
  U3130_HONORARIUM_ROW_CELL_PY,
  U3130_HONORARIUM_TASK_ROW_BOX_CLASS,
  U3130_HONORARIUM_TASK_ROW_META_COLSPAN,
  U3130_HONORARIUM_TASK_ROW_NATURE_PART_CLASS,
  U3130_HONORARIUM_TASK_ROW_SPLIT_WRAPPER_CLASS,
  U3130_HONORARIUM_TASK_ROW_TASK_PART_CLASS,
  U3130_READONLY_TABLE_CELL_BOX_COMPACT_CLASS,
} from '../../tools/u3130HonorariumGridCompactStyles';

export interface U3130HonorariumReadonlyTaskRowProps {
  index: number;
  row: U3130HonorariumItem;
  formStateHandlers: U2000FormStateHandlers<U3130Timesheet>;
  visibleDays: readonly U2000TimesheetWeekDay[];
  showSubProjectColumn: boolean;
  formatHours: (value: number) => string;
}

function rowTotalHours(row: U3130HonorariumItem): number {
  return U2000_TIMESHEET_WEEK_DAYS.reduce((acc, d) => acc + (Number(row[d]) || 0), 0);
}

/** Ligne tâche d’un dossier déplié (rendu U3129) — affichage seulement. */
const U3130HonorariumReadonlyTaskRow: React.FC<U3130HonorariumReadonlyTaskRowProps> = ({
  index,
  row,
  formStateHandlers,
  visibleDays,
  showSubProjectColumn,
  formatHours,
}) => {
  const { i18n } = useTranslation();
  const natures = u3130TaskWorkNatureDescriptionForLanguage(row, i18n.language);
  const subprojectLabel = u3130HonorariumSubprojectLabel(row);

  const boxClass = `${U3130_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} ${U3130_HONORARIUM_EXPANDED_CHILD_BOX_CLASS}`;
  const taskBoxClass = `${U3130_HONORARIUM_TASK_ROW_BOX_CLASS} ${U3130_HONORARIUM_EXPANDED_CHILD_BOX_CLASS}`;

  return (
    <tr className={U3130_HONORARIUM_EXPANDED_CHILD_ROW_CLASS}>
      <td className={`${U3130_HONORARIUM_CHECKBOX_TD_CLASS} ${U3130_HONORARIUM_EXPANDED_ACCENT_CELL_CLASS}`}>
        <U2000CheckBox formStateHandlers={formStateHandlers} fieldName={`honoraria.${index}.checked`} label="" readonly />
      </td>
      <td colSpan={U3130_HONORARIUM_TASK_ROW_META_COLSPAN} className={`${U3130_HONORARIUM_CELL_X} ${U3130_HONORARIUM_ROW_CELL_PY} align-top text-left`}>
        <div className={U3130_HONORARIUM_TASK_ROW_SPLIT_WRAPPER_CLASS}>
          <div className={U3130_HONORARIUM_TASK_ROW_TASK_PART_CLASS}>
            <div className={`${taskBoxClass} break-words`}>{u3130HonorariumTaskLabel(row) || '—'}</div>
          </div>
          <div className={U3130_HONORARIUM_TASK_ROW_NATURE_PART_CLASS}>
            <div className={`${taskBoxClass} break-all`}>{natures}</div>
          </div>
        </div>
      </td>
      {showSubProjectColumn ? (
        <td className={`${U3130_HONORARIUM_CELL_X} ${U3130_HONORARIUM_ROW_CELL_PY} align-top`}>
          <div className={`${boxClass} truncate`} title={subprojectLabel || undefined}>
            {subprojectLabel}
          </div>
        </td>
      ) : null}
      {visibleDays.map((d) => (
        <td key={d} className={`${U3130_HONORARIUM_HOUR_TD_CLASS} align-top text-right`}>
          <div className={`${boxClass} justify-end tabular-nums`}>{formatHours(Number(row[d]) || 0)}</div>
        </td>
      ))}
      <td className={`${U3130_HONORARIUM_HOUR_TD_CLASS} align-top text-right`}>
        <div className={`${boxClass} justify-end tabular-nums`}>{formatHours(rowTotalHours(row))}</div>
      </td>
    </tr>
  );
};

export default U3130HonorariumReadonlyTaskRow;
